import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import BaseWrapper from "../../components/shared/BaseWrapper";
import jsPDF from "jspdf";
import { getTechnicianPayrateCredits } from "../API/technicianPayrateCreditsAPI";


// Convert string → number with 2 decimal places
const parseNumberInput = (value) => {
  if (!value) return "";
  const cleaned = value.replace(/,/g, "");
  let num = parseFloat(cleaned);
  if (isNaN(num)) return "";
  return parseFloat(num.toFixed(2));  // force 2 decimal places
};

// Format number with commas for display
const formatWithCommas = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  return isNaN(num)
    ? value
    : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};


function PayrollFinalCalculation() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    calculationData,
    searchFilter,
    hourlyRate,
    additionalIncomes,
    deductions,
    rateMultipliers,
  } = location.state || {};

  const [exportMenu, setExportMenu] = useState(null);

  const handleOpenExportMenu = (event) => {
    setExportMenu(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenu(null);
  };

  const [payDate, setPayDate] = useState(() => {
    // Default to end of the pay period if available, otherwise today (YYYY-MM-DD)
    if (searchFilter?.endDate) return searchFilter.endDate;
    return new Date().toISOString().split("T")[0];
  });

  // --- HOURS coming from PayrollReports (hours-only payload) ---
  const {
    breakdownTotals = {},
    grandTotalHours = 0,
    totalDeductedHours = 0,
    uniqueGrandTotalHours = 0,
    totalAddWorkHours = 0,
  } = calculationData || {};

  const safeBreakdownTotals = {
    normalHours: Number(breakdownTotals.normalHours || 0),
    nightDifferential: Number(breakdownTotals.nightDifferential || 0),
    regularNightDifferential: Number(
      breakdownTotals.regularNightDifferential || 0
    ),
    overtimeHours: Number(
      breakdownTotals.overtimeHours ?? breakdownTotals.overtime ?? 0
    ),
    regularHolidayHours: Number(
      breakdownTotals.regularHolidayHours ??
        breakdownTotals.regularHoliday ??
        0
    ),
    specialHolidayHours: Number(
      breakdownTotals.specialHolidayHours ??
        breakdownTotals.specialHoliday ??
        0
    ),
    customEntries: breakdownTotals.customEntries || {},
  };

  const customEntriesArray = Object.entries(
    safeBreakdownTotals.customEntries || {}
  );

  // --- Local, editable rate multipliers on this page ---
  const [localRateMultipliers, setLocalRateMultipliers] = useState(() => ({
    normalHours: rateMultipliers?.normalHours ?? 1,
    nightDifferential: rateMultipliers?.nightDifferential ?? 0,
    overtime: rateMultipliers?.overtime ?? 0,
    regularHoliday: rateMultipliers?.regularHoliday ?? 0,
    specialHoliday: rateMultipliers?.specialHoliday ?? 0,
    // addWorkHours is currently not used
    addWorkHours: rateMultipliers?.addWorkHours ?? 1,
    customRates: rateMultipliers?.customRates || {},
  }));

  // --- Local, editable additional incomes on this page ---
  const [localAdditionalIncomes, setLocalAdditionalIncomes] = useState(() => ({
    allowance: additionalIncomes?.allowance ?? "",
    bonus: additionalIncomes?.bonus ?? "",
    thirteenthMonthPay: additionalIncomes?.thirteenthMonthPay ?? "",
    customIncomes: additionalIncomes?.customIncomes
      ? additionalIncomes.customIncomes.map((ci) => ({
          label: ci.label || "",
          amount: ci.amount || "",
        }))
      : [],
  }));

  const handleAdditionalIncomeChange = (field, value) => {
    setLocalAdditionalIncomes((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddCustomIncome = () => {
    setLocalAdditionalIncomes((prev) => ({
      ...prev,
      customIncomes: [
        ...(prev.customIncomes || []),
        { label: "", amount: "" },
      ],
    }));
  };

  const handleCustomIncomeChange = (index, field, value) => {
    setLocalAdditionalIncomes((prev) => {
      const next = [...(prev.customIncomes || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, customIncomes: next };
    });
  };

  const handleRemoveCustomIncome = (index) => {
    setLocalAdditionalIncomes((prev) => {
      const next = [...(prev.customIncomes || [])];
      next.splice(index, 1);
      return { ...prev, customIncomes: next };
    });
  };

  const [localDeductions, setLocalDeductions] = useState(() => ({
    sss: deductions?.sss ?? "",
    sssProvident: deductions?.sssProvident ?? "",
    philhealth: deductions?.philhealth ?? "",
    pagIbig: deductions?.pagIbig ?? "",
    unionDues: deductions?.unionDues ?? "",
    tax: deductions?.tax ?? "",
    pagIbig2: deductions?.pagIbig2 ?? "",
    deductedHoursReason: deductions?.deductedHoursReason ?? "",
    customDeductions: deductions?.customDeductions
      ? deductions.customDeductions.map((cd) => ({
          label: cd.label || "",
          amount: cd.amount || "",
        }))
      : [],

    // --- YTD balances (for reference only, not included in total deductions) ---
    loanBalance: deductions?.loanBalance ?? "",
    leaveBalance: deductions?.leaveBalance ?? "",
    customBalances: deductions?.customBalances
      ? deductions.customBalances.map((cb) => ({
          label: cb.label || "",
          amount: cb.amount || "",
        }))
      : [],
  }));


  const handleDeductionChange = (field, value) => {
    setLocalDeductions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddCustomDeduction = () => {
    setLocalDeductions((prev) => ({
      ...prev,
      customDeductions: [
        ...(prev.customDeductions || []),
        { label: "", amount: "" },
      ],
    }));
  };

  const handleCustomDeductionChange = (index, field, value) => {
    setLocalDeductions((prev) => {
      const next = [...(prev.customDeductions || [])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, customDeductions: next };
    });
  };

  const handleRemoveCustomDeduction = (index) => {
    setLocalDeductions((prev) => {
      const next = [...(prev.customDeductions || [])];
      next.splice(index, 1);
      return { ...prev, customDeductions: next };
    });
  };

  const handleYtdBalanceChange = (field, value) => {
    setLocalDeductions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddCustomYtdBalance = () => {
    setLocalDeductions((prev) => ({
      ...prev,
      customBalances: [
        ...(prev.customBalances || []),
        { label: "", amount: "" },
      ],
    }));
  };

  const handleCustomYtdBalanceChange = (index, field, value) => {
    setLocalDeductions((prev) => {
      const next = [...(prev.customBalances || [])];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, customBalances: next };
    });
  };

  const handleRemoveCustomYtdBalance = (index) => {
    setLocalDeductions((prev) => {
      const next = [...(prev.customBalances || [])];
      next.splice(index, 1);
      return { ...prev, customBalances: next };
    });
  };


  // === BASIC RATE (editable by user) ===
  const [basicRate, setBasicRate] = useState(() => {
    const passed = parseFloat(hourlyRate);
    return !isNaN(passed) ? passed : 0;
  });

  // === SIL CREDITS (auto-populated from API) ===
  const [silCredits, setSilCredits] = useState(0);

  // Auto-populate basic pay rate and SIL credits from API
  useEffect(() => {
    const fetchPayrateCredits = async () => {
      // Get technician ID from searchFilter
      const techId = searchFilter?.employee?.id;
      
      if (!techId) {
        return;
      }

      try {
        const data = await getTechnicianPayrateCredits(techId);
        
        if (data) {
          // Auto-populate basic pay rate if not already set
          if (data.basic_pay_rate && (!hourlyRate || hourlyRate === 0)) {
            setBasicRate(parseFloat(data.basic_pay_rate) || 0);
          }
          
          // Always update SIL credits to reflect latest balance
          if (data.leave_credits !== undefined) {
            const credits = parseFloat(data.leave_credits) || 0;

            setSilCredits(credits);

            setLocalDeductions(prev => ({
              ...prev,
              leaveBalance: credits   // ✅ now credits actually exists
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching payrate credits:", error);
      }
    };

    fetchPayrateCredits();
  }, [searchFilter?.employee?.id]);

  // base rate now comes from user-editable input
  const baseRate = useMemo(
    () => parseFloat(basicRate) || 0,
    [basicRate]
  );

  // --- Derived PAY calculations (gross, additional income, deductions, net) ---
  const paySummary = useMemo(() => {
    const nh = safeBreakdownTotals.normalHours;
    const ndh = safeBreakdownTotals.nightDifferential;
    const rnh = safeBreakdownTotals.regularNightDifferential;
    const ot = safeBreakdownTotals.overtimeHours;
    const rh = safeBreakdownTotals.regularHolidayHours;
    const sh = safeBreakdownTotals.specialHolidayHours;

    const normalMult = parseFloat(localRateMultipliers.normalHours) || 0;
    const nightMult =
      parseFloat(localRateMultipliers.nightDifferential) || 0;
    const overtimeMult = parseFloat(localRateMultipliers.overtime) || 0;
    const regHolMult =
      parseFloat(localRateMultipliers.regularHoliday) || 0;
    const specHolMult =
      parseFloat(localRateMultipliers.specialHoliday) || 0;
    const addWorkMult =
      parseFloat(localRateMultipliers.addWorkHours) || 0;

    const normalPay = nh * baseRate * normalMult;
    const nightDiffPay = ndh * baseRate * nightMult;
    const regularNightDiffPay = rnh * baseRate * nightMult;
    const overtimePay = ot * baseRate * overtimeMult;
    const regularHolidayPay = rh * baseRate * regHolMult;
    const specialHolidayPay = sh * baseRate * specHolMult;
    const addWorkHoursPay =
      Number(totalAddWorkHours || 0) * baseRate * addWorkMult;

    const customPays = customEntriesArray.map(([label, hours]) => {
      const mult =
        parseFloat(localRateMultipliers.customRates?.[label]) || 0;
      const h = Number(hours || 0);
      return {
        label,
        hours: h,
        pay: h * baseRate * mult,
      };
    });
    const customPayTotal = customPays.reduce((sum, cp) => sum + cp.pay, 0);

    const grossPay =
      normalPay +
      nightDiffPay +
      regularNightDiffPay +
      overtimePay +
      regularHolidayPay +
      specialHolidayPay +
      addWorkHoursPay +
      customPayTotal;

    // --- Additional incomes (from localAdditionalIncomes) ---
    const allowance =
      parseFloat(localAdditionalIncomes?.allowance) || 0;
    const bonus = parseFloat(localAdditionalIncomes?.bonus) || 0;
    const thirteenth =
      parseFloat(localAdditionalIncomes?.thirteenthMonthPay) || 0;

    const customIncomesTotal = (
      localAdditionalIncomes?.customIncomes || []
    ).reduce((sum, inc) => sum + (parseFloat(inc.amount) || 0), 0);

    const totalAdditional =
      allowance + bonus + thirteenth + customIncomesTotal;

    const grossWithAdditional = grossPay + totalAdditional;

    // --- Deductions (from localDeductions) ---
    // IMPORTANT: totalDeductedHours is *informational only*, not used here.
    const fixedDeductions =
      (parseFloat(localDeductions?.sss) || 0) +
      (parseFloat(localDeductions?.sssProvident) || 0) +
      (parseFloat(localDeductions?.philhealth) || 0) +
      (parseFloat(localDeductions?.pagIbig) || 0) +
      (parseFloat(localDeductions?.unionDues) || 0) +
      (parseFloat(localDeductions?.tax) || 0) +
      (parseFloat(localDeductions?.pagIbig2) || 0);

    const customDeductionsTotal = (
      localDeductions?.customDeductions || []
    ).reduce((sum, ded) => sum + (parseFloat(ded.amount) || 0), 0);

    const totalDeductions = fixedDeductions + customDeductionsTotal;

    const netPay = grossWithAdditional - totalDeductions;

    return {
      normalPay,
      nightDiffPay,
      regularNightDiffPay,
      overtimePay,
      regularHolidayPay,
      specialHolidayPay,
      addWorkHoursPay,
      customPays,
      grossPay,
      totalAdditional,
      grossWithAdditional,
      fixedDeductions,
      customDeductionsTotal,
      totalDeductions,
      netPay,
    };
  }, [
    safeBreakdownTotals.normalHours,
    safeBreakdownTotals.nightDifferential,
    safeBreakdownTotals.regularNightDifferential,
    safeBreakdownTotals.overtimeHours,
    safeBreakdownTotals.regularHolidayHours,
    safeBreakdownTotals.specialHolidayHours,
    totalAddWorkHours,
    customEntriesArray,
    localRateMultipliers.normalHours,
    localRateMultipliers.nightDifferential,
    localRateMultipliers.overtime,
    localRateMultipliers.regularHoliday,
    localRateMultipliers.specialHoliday,
    localRateMultipliers.addWorkHours,
    localRateMultipliers.customRates,
    baseRate,
    localAdditionalIncomes,
    localDeductions,
  ]);

  const {
    normalPay,
    nightDiffPay,
    regularNightDiffPay,
    overtimePay,
    regularHolidayPay,
    specialHolidayPay,
    addWorkHoursPay,
    customPays,
    grossPay,
    totalAdditional,
    grossWithAdditional,
    fixedDeductions,
    customDeductionsTotal,
    totalDeductions,
    netPay,
  } = paySummary;

  // === CSV Export for FINAL calculation ===
  const handleFinalCSVExport = () => {
    const currentDate = new Date();
    const reportDate = `${String(currentDate.getMonth() + 1).padStart(
      2,
      "0"
    )}/${String(currentDate.getDate()).padStart(2, "0")}/${currentDate.getFullYear()}`;

    const csvRows = [];

    // HEADER
    csvRows.push("BOAZ Pest Management Corp.");
    csvRows.push("PAYSLIP FINAL CALCULATION");
    csvRows.push("");    
    csvRows.push(
      `Period,${searchFilter?.startDate || ""} to ${
        searchFilter?.endDate || ""
      }`
    );
    csvRows.push(`Pay Date,${payDate}`);
    csvRows.push(`Generated,${reportDate}`);
    csvRows.push(
      `Employee,"${searchFilter?.employee?.name || "N/A"}"`
    );
    csvRows.push(`Basic Rate,${baseRate}`);
    csvRows.push("");

    // HOURS BREAKDOWN SUMMARY
    csvRows.push("HOURS BREAKDOWN SUMMARY");
    csvRows.push("Category,Hours,Rate Multiplier,Amount");

    const fmtAmount = (value) =>
      `PHP ${Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

    const pushCategory = (label, hours, multiplier, amount) => {
      if (!hours || Number(hours) <= 0) return;
      const h = Number(hours).toFixed(2);
      const m =
        multiplier !== undefined && multiplier !== null && multiplier !== ""
          ? Number(multiplier).toFixed(2)
          : "";
      csvRows.push(
        `${label},${h},${m},"${fmtAmount(amount)}"`
      );
    };

    // Standard categories (use new safeBreakdownTotals + localRateMultipliers)
    const normalMult =
      parseFloat(localRateMultipliers.normalHours) || 0;
    const nightMult =
      parseFloat(localRateMultipliers.nightDifferential) || 0;
    const overtimeMult =
      parseFloat(localRateMultipliers.overtime) || 0;
    const regHolMult =
      parseFloat(localRateMultipliers.regularHoliday) || 0;
    const specHolMult =
      parseFloat(localRateMultipliers.specialHoliday) || 0;
    const addWorkMult =
      parseFloat(localRateMultipliers.addWorkHours) || 0;

    // Normal Hours
    pushCategory(
      "Normal Hours",
      safeBreakdownTotals.normalHours,
      normalMult,
      baseRate *
        normalMult *
        safeBreakdownTotals.normalHours
    );

    // Night Differential
    pushCategory(
      "Night Differential",
      safeBreakdownTotals.nightDifferential,
      nightMult,
      baseRate *
        nightMult *
        safeBreakdownTotals.nightDifferential
    );

    // Regular Night Differential (new category)
    // pushCategory(
    //   "Regular Night Differential",
    //   safeBreakdownTotals.regularNightDifferential,
    //   nightMult,
    //   baseRate *
    //     nightMult *
    //     safeBreakdownTotals.regularNightDifferential
    // );

    // Overtime
    pushCategory(
      "Overtime",
      safeBreakdownTotals.overtimeHours,
      overtimeMult,
      baseRate *
        overtimeMult *
        safeBreakdownTotals.overtimeHours
    );

    // Regular Holiday
    pushCategory(
      "Regular Holiday",
      safeBreakdownTotals.regularHolidayHours,
      regHolMult,
      baseRate *
        regHolMult *
        safeBreakdownTotals.regularHolidayHours
    );

    // Special Non-Working Holiday
    pushCategory(
      "Special Non-Working Holiday",
      safeBreakdownTotals.specialHolidayHours,
      specHolMult,
      baseRate *
        specHolMult *
        safeBreakdownTotals.specialHolidayHours
    );

    // Manually Added Work Hours (if any)
    // if (Number(totalAddWorkHours || 0) > 0) {
    //   pushCategory(
    //     "Manually Added Work Hours",
    //     totalAddWorkHours,
    //     addWorkMult,
    //     baseRate * addWorkMult * Number(totalAddWorkHours || 0)
    //   );
    // }

    // Custom entries (based on customPays + localRateMultipliers.customRates)
    customPays.forEach((cp) => {
      if (!cp || !cp.label) return;
      const label = cp.label;
      const hours = cp.hours || 0;
      if (!hours || hours <= 0) return;

      const customMult =
        parseFloat(localRateMultipliers.customRates?.[label]) ||
        (baseRate && hours
          ? cp.pay / (baseRate * hours)
          : 0);

      pushCategory(label, hours, customMult, cp.pay);
    });

    csvRows.push("");
    csvRows.push(
      `Total Hours,${Number(grandTotalHours || 0).toFixed(2)}`
    );
    csvRows.push(
      `Gross Pay,"${fmtAmount(grossPay)}"`
    );
    csvRows.push("");

    // ADDITIONAL INCOMES (using localAdditionalIncomes)
    if (totalAdditional > 0) {
      csvRows.push("ADDITIONAL INCOMES");

      const allowanceVal =
        parseFloat(localAdditionalIncomes.allowance) || 0;
      const bonusVal =
        parseFloat(localAdditionalIncomes.bonus) || 0;
      const thirteenthVal =
        parseFloat(localAdditionalIncomes.thirteenthMonthPay) || 0;

      if (allowanceVal > 0) {
        csvRows.push(
          `Allowance,"${fmtAmount(allowanceVal)}"`
        );
      }
      if (bonusVal > 0) {
        csvRows.push(
          `Bonus,"${fmtAmount(bonusVal)}"`
        );
      }
      if (thirteenthVal > 0) {
        csvRows.push(
          `13th Month Pay,"${fmtAmount(thirteenthVal)}"`
        );
      }

      (localAdditionalIncomes.customIncomes || []).forEach(
        (custom) => {
          if (!custom?.label) return;
          const amt = parseFloat(custom.amount) || 0;
          if (amt > 0) {
            csvRows.push(
              `${custom.label},"${fmtAmount(amt)}"`
            );
          }
        }
      );

      csvRows.push(
        `Total Additional Incomes,"${fmtAmount(
          totalAdditional
        )}"`
      );
      csvRows.push("");
    }

    // FINAL GROSS PAY = grossPay + additional (grossWithAdditional)
    csvRows.push(
      `FINAL GROSS PAY,"${fmtAmount(grossWithAdditional)}"`
    );
    csvRows.push("");

    // DEDUCTIONS
    csvRows.push("DEDUCTIONS");

    // Deducted Hours row: label, HOURS ONLY, and reason (from localDeductions)
    if (Number(totalDeductedHours || 0) > 0) {
      const hoursStr = Number(totalDeductedHours || 0).toFixed(2);

      const reason =
        localDeductions?.deductedHoursReason &&
        String(localDeductions.deductedHoursReason).trim()
          ? String(localDeductions.deductedHoursReason).trim()
          : "";

      // 1) "Deducted Hours"
      // 2) <number of hours>
      // 3) <reason text>
      csvRows.push(`Deducted Hours,${hoursStr},"${reason}"`);
    }

    // Helper to push each monetary deduction as its own line
    const pushDeduction = (label, value) => {
      const v = parseFloat(value);
      if (!v || Number.isNaN(v) || v <= 0) return;
      csvRows.push(`${label},"${fmtAmount(v)}"`);
    };

    // Standard deductions FROM localDeductions (what the user sees/edits)
    pushDeduction("SSS", localDeductions?.sss);
    pushDeduction("SSS Provident", localDeductions?.sssProvident);
    pushDeduction("PhilHealth", localDeductions?.philhealth);
    pushDeduction("Pag-IBIG", localDeductions?.pagIbig);
    pushDeduction("Union Dues", localDeductions?.unionDues);
    pushDeduction("Tax", localDeductions?.tax);
    pushDeduction("Pag-IBIG 2", localDeductions?.pagIbig2);

    // Custom deductions from user input (also from localDeductions)
    (localDeductions?.customDeductions || []).forEach((cd) => {
      if (!cd?.label) return;
      const amt = parseFloat(cd.amount);
      if (!amt || Number.isNaN(amt) || amt <= 0) return;

      csvRows.push(`${cd.label},"${fmtAmount(amt)}"`);
    });

    // Total (computed from paySummary which already uses localDeductions)
    csvRows.push(
      `Total Deductions,"${fmtAmount(totalDeductions)}"`
    );
    csvRows.push("");


    // NET PAY
    csvRows.push(`NET PAY,"${fmtAmount(netPay)}"`);
    csvRows.push("");
    csvRows.push("");

        // YTD BALANCE SECTION – based on current UI values (localDeductions)
    csvRows.push("YTD BALANCE");
    csvRows.push("");

    // Leave Credits (YTD) – single numeric value from localDeductions.leaveBalance
    const leaveBalRaw = silCredits;
    const hasLeaveBal =
      leaveBalRaw !== undefined &&
      leaveBalRaw !== null &&
      String(leaveBalRaw).trim() !== "";

    if (hasLeaveBal) {
      csvRows.push(
        `Leave Credits (YTD),${String(leaveBalRaw).trim()}`
      );
    } else {
      csvRows.push("Leave Credits (YTD),No value");
    }

    // Custom YTD Balances – each row uses the label entered by the user
    const customYtdBalances = localDeductions?.customBalances || [];
    if (customYtdBalances.length > 0) {
      csvRows.push("");
      csvRows.push("OTHER YTD BALANCES");

      customYtdBalances.forEach((bal) => {
        if (!bal) return;
        const label = (bal.label || "").trim() || "Unlabeled";
        const amountHasValue =
          bal.amount !== undefined &&
          bal.amount !== null &&
          String(bal.amount).trim() !== "";
        let amountStr = amountHasValue
          ? String(bal.amount).trim()
          : "No value";

        // Wrap in quotes if value contains a comma or space
        if (amountStr.includes(",") || amountStr.includes(" ")) {
          amountStr = `"${amountStr}"`;
        }

        // No "PHP" prefix here – these can be hours, pesos, etc.,
        // depending on how you use the field.
        csvRows.push(`${label},${amountStr}`);
      });
    }


    // Build and download CSV
    const csvContent = csvRows.join("\r\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const safeName = (searchFilter?.employee?.name || "technician")
      .replace(/[^\w\- ]/g, "")
      .replace(/\s+/g, "_");

    link.setAttribute(
      "download",
      `FINAL_CALCULATION_${safeName}_${
        searchFilter?.startDate || ""
      }_to_${searchFilter?.endDate || ""}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // === PDF Export for FINAL calculation – 2-column layout with better spacing ===
  const handleFinalPDFExport = () => {
    try {
      const doc = new jsPDF();

      // Layout constants
      const pageWidth = 210;
      const margin = 15;
      const columnGap = 8;
      const columnWidth = (pageWidth - margin * 2 - columnGap) / 2;
      const leftX = margin;
      const rightX = margin + columnWidth + columnGap;

      // Tighter line height so lines don't look too far apart
      const lineHeight = 4.5;

      const formatPeso = (value) =>
        `PHP ${Number(value || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

      const formatDate = (d) =>
        d
          ? new Date(d).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "N/A";

      // ===========================
      // HEADER (dark blue bar)
      // ===========================
      doc.setFillColor(23, 42, 74); // your dark blue
      doc.rect(0, 0, pageWidth, 18, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "normal");
      // Small header font
      doc.setFontSize(8);
      doc.text(
        "BOAZ PEST MANAGEMENT CORP. - PAYSLIP FINAL CALCULATION",
        pageWidth / 2,
        11,
        { align: "center" }
      );

      // Sizes for the rest
      const titleFont = 7; // section titles
      const labelFont = 6; // labels / small headings
      const bodyFont = 5.5; // values

      doc.setTextColor(0, 0, 0);

      // ===========================
      // ROW 1 LEFT: EMPLOYEE & REPORT INFO (single box)
      // ===========================
      const row1Top = 22;
      const row1BoxHeight = 44; // a bit taller + tighter lines

      doc.setDrawColor(30, 93, 69);
      doc.setLineWidth(0.3);
      doc.setFillColor(245, 250, 248);
      doc.roundedRect(leftX, row1Top, columnWidth, row1BoxHeight, 2, 2, "FD");

      let y = row1Top + 7; // start content slightly lower inside box

      // Employee Information
      doc.setFont("helvetica", "bold");
      doc.setFontSize(titleFont);
      doc.setTextColor(30, 93, 69);
      doc.text("EMPLOYEE INFORMATION", leftX + 3, y);

      // Lines under EMPLOYEE INFORMATION
      y += lineHeight;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(labelFont);
      doc.setTextColor(0, 0, 0);
      doc.text("Name:", leftX + 3, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(bodyFont);
      doc.text(
        searchFilter?.employee?.name || "N/A",
        leftX + 22,
        y
      );

      y += lineHeight;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(labelFont);
      doc.text("Department:", leftX + 3, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(bodyFont);
      doc.text(
        searchFilter?.employee?.department || "Operations",
        leftX + 22,
        y
      );

      // Small gap then REPORT DETAILS (still inside same box)
      y += lineHeight * 2;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(titleFont);
      doc.setTextColor(30, 93, 69);
      doc.text("REPORT DETAILS", leftX + 3, y);

      y += lineHeight;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(labelFont);
      doc.setTextColor(0, 0, 0);
      doc.text("Pay Period:", leftX + 3, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(bodyFont);
      doc.text(
        `${formatDate(searchFilter?.startDate)} - ${formatDate(
          searchFilter?.endDate
        )}`,
        leftX + 22,
        y
      );

      y += lineHeight;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(labelFont);
      doc.text("Pay Date:", leftX + 3, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(bodyFont);
      doc.text(formatDate(payDate), leftX + 22, y);

      y += lineHeight;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(labelFont);
      doc.text("Generated:", leftX + 3, y);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(bodyFont);
      doc.text(formatDate(new Date()), leftX + 22, y);

      const leftRow1Bottom = row1Top + row1BoxHeight;

      // ===========================
      // ROW 1 RIGHT: HOURS BREAKDOWN & COMPUTATION
      // ===========================
      let yRight = row1Top;

      // Section header
      doc.setFillColor(30, 93, 69);
      doc.rect(rightX, yRight, columnWidth, 6, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(titleFont);
      doc.text(
        "I. HOURS BREAKDOWN & COMPUTATION",
        rightX + 3,
        yRight + 4
      );

      yRight += 9;
      doc.setTextColor(0, 0, 0);

      // Base rate row
      doc.setFillColor(250, 250, 250);
      doc.rect(rightX, yRight, columnWidth, 6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont);
      doc.text("Basic Rate:", rightX + 3, yRight + 4);
      doc.setFontSize(bodyFont);
      doc.text(formatPeso(baseRate), rightX + columnWidth - 2, yRight + 4, {
        align: "right",
      });

      yRight += 8;

      // Table header
      doc.setFillColor(240, 240, 240);
      doc.rect(rightX, yRight, columnWidth, 6, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont);
      doc.setTextColor(60, 60, 60);

      const colCatX = rightX + 3;
      const colHoursX = rightX + columnWidth * 0.45;
      const colMultX = rightX + columnWidth * 0.7;
      const colAmtX = rightX + columnWidth - 2;

      doc.text("Category", colCatX, yRight + 4);
      doc.text("Hrs", colHoursX, yRight + 4, { align: "center" });
      doc.text("Mult", colMultX, yRight + 4, { align: "center" });
      doc.text("Amount", colAmtX, yRight + 4, { align: "right" });

      yRight += 9;
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(bodyFont);

      const pushHoursRow = (label, hours, multiplier, amount) => {
        if (!hours || Number(hours) <= 0) return;
        const h = Number(hours).toFixed(2);
        const m =
          multiplier !== undefined && multiplier !== null
            ? Number(multiplier).toFixed(2)
            : "";
        const amt = formatPeso(amount || 0);

        doc.text(label, colCatX, yRight);
        doc.text(h, colHoursX, yRight, { align: "center" });
        doc.text(m, colMultX, yRight, { align: "center" });
        doc.text(amt, colAmtX, yRight, { align: "right" });

        yRight += lineHeight;
      };

      const normalMult = parseFloat(localRateMultipliers.normalHours) || 0;
      const nightMult =
        parseFloat(localRateMultipliers.nightDifferential) || 0;
      const overtimeMult = parseFloat(localRateMultipliers.overtime) || 0;
      const regHolMult =
        parseFloat(localRateMultipliers.regularHoliday) || 0;
      const specHolMult =
        parseFloat(localRateMultipliers.specialHoliday) || 0;

      // Normal Hours
      pushHoursRow(
        "Normal",
        safeBreakdownTotals.normalHours,
        normalMult,
        safeBreakdownTotals.normalHours * baseRate * normalMult
      );

      // Night Differential
      pushHoursRow(
        "Night Diff",
        safeBreakdownTotals.nightDifferential,
        nightMult,
        safeBreakdownTotals.nightDifferential * baseRate * nightMult
      );

      // Regular Night Differential
      pushHoursRow(
        "Regular Night Diff",
        safeBreakdownTotals.regularNightDifferential,
        nightMult,
        safeBreakdownTotals.regularNightDifferential * baseRate * nightMult
      );

      // Overtime
      pushHoursRow(
        "Overtime",
        safeBreakdownTotals.overtimeHours,
        overtimeMult,
        safeBreakdownTotals.overtimeHours * baseRate * overtimeMult
      );

      // Regular Holiday
      pushHoursRow(
        "Regular Holiday",
        safeBreakdownTotals.regularHolidayHours,
        regHolMult,
        safeBreakdownTotals.regularHolidayHours * baseRate * regHolMult
      );

      // Special Holiday
      pushHoursRow(
        "Special Holiday",
        safeBreakdownTotals.specialHolidayHours,
        specHolMult,
        safeBreakdownTotals.specialHolidayHours * baseRate * specHolMult
      );

      // Custom categories
      customPays.forEach((cp) => {
        if (!cp || !cp.label) return;
        if (!cp.hours || cp.hours <= 0) return;
        let mult =
          parseFloat(localRateMultipliers.customRates?.[cp.label]) || 0;
        if (!mult && baseRate && cp.hours) {
          mult = cp.pay / (baseRate * cp.hours);
        }
        pushHoursRow(cp.label, cp.hours, mult, cp.pay);
      });

      // Total hours + gross
      yRight += 2;
      doc.setDrawColor(30, 93, 69);
      doc.setLineWidth(0.3);
      doc.line(rightX, yRight, rightX + columnWidth, yRight);
      yRight += 4;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont);
      doc.text("Total Hours:", colCatX, yRight);
      doc.setFontSize(bodyFont);
      doc.text(
        Number(grandTotalHours || 0).toFixed(2),
        colHoursX,
        yRight,
        { align: "center" }
      );

      yRight += lineHeight + 1;

      doc.setFillColor(245, 250, 248);
      doc.roundedRect(rightX, yRight - 4, columnWidth, 7, 1, 1, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont);
      doc.setTextColor(30, 93, 69);
      doc.text("GROSS PAY:", colCatX, yRight + 1);
      doc.text(formatPeso(grossPay), colAmtX, yRight + 1, {
        align: "right",
      });

      const rightRow1Bottom = yRight + 6;

      // Use the LOWER of the two row bottoms + margin so rows never overlap
      const row1Bottom = Math.max(leftRow1Bottom, rightRow1Bottom);
      const row2Top = row1Bottom + 20;

      // ===========================
      // ROW 2 LEFT: ADDITIONAL INCOME + FINAL GROSS PAY
      // ===========================
      y = row2Top;

      doc.setFillColor(30, 93, 69);
      doc.rect(leftX, y, columnWidth, 6, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(titleFont);
      doc.text("II. ADDITIONAL INCOMES", leftX + 3, y + 4);

      y += 9;
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(bodyFont);

      const allowance =
        parseFloat(localAdditionalIncomes?.allowance) || 0;
      const bonus = parseFloat(localAdditionalIncomes?.bonus) || 0;
      const thirteenth =
        parseFloat(localAdditionalIncomes?.thirteenthMonthPay) || 0;

      if (allowance > 0) {
        doc.text("Allowance", leftX + 3, y);
        doc.text(formatPeso(allowance), leftX + columnWidth - 2, y, {
          align: "right",
        });
        y += lineHeight;
      }

      if (bonus > 0) {
        doc.text("Bonus", leftX + 3, y);
        doc.text(formatPeso(bonus), leftX + columnWidth - 2, y, {
          align: "right",
        });
        y += lineHeight;
      }

      if (thirteenth > 0) {
        doc.text("13th Month Pay", leftX + 3, y);
        doc.text(formatPeso(thirteenth), leftX + columnWidth - 2, y, {
          align: "right",
        });
        y += lineHeight;
      }

      (localAdditionalIncomes?.customIncomes || []).forEach((ci) => {
        const amt = parseFloat(ci?.amount) || 0;
        if (!ci?.label || amt <= 0) return;
        doc.text(ci.label, leftX + 3, y);
        doc.text(formatPeso(amt), leftX + columnWidth - 2, y, {
          align: "right",
        });
        y += lineHeight;
      });

      y += 3;
      doc.setDrawColor(30, 93, 69);
      doc.setLineWidth(0.3);
      doc.line(leftX, y, leftX + columnWidth, y);
      y += 4;

      doc.setFillColor(245, 250, 248);
      doc.roundedRect(leftX, y - 4, columnWidth, 7, 1, 1, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont);
      doc.setTextColor(30, 93, 69);
      doc.text("TOTAL ADDITIONAL INCOMES:", leftX + 3, y + 1);
      doc.text(
        formatPeso(totalAdditional),
        leftX + columnWidth - 2,
        y + 1,
        { align: "right" }
      );

      // FINAL GROSS PAY (under additional incomes)
      y += 9;
      doc.setFillColor(30, 93, 69);
      doc.roundedRect(leftX, y, columnWidth, 8, 2, 2, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont);
      doc.text("FINAL GROSS PAY", leftX + 3, y + 5);
      doc.text(
        formatPeso(grossWithAdditional),
        leftX + columnWidth - 2,
        y + 5,
        { align: "right" }
      );

      const leftRow2Bottom = y + 10;

      // ===========================
      // ROW 2 RIGHT: DEDUCTIONS + TOTAL DEDUCTIONS
      // ===========================
      yRight = row2Top;

      const hasAnyDeductions =
        totalDeductions > 0 || Number(totalDeductedHours || 0) > 0;

      let rightRow2Bottom = yRight;
      if (hasAnyDeductions) {
        doc.setFillColor(200, 50, 50);
        doc.rect(rightX, yRight, columnWidth, 6, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(titleFont);
        doc.text("III. DEDUCTIONS", rightX + 3, yRight + 4);

        yRight += 9;
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(bodyFont);

        const govHasAny =
          (parseFloat(localDeductions?.sss) || 0) > 0 ||
          (parseFloat(localDeductions?.sssProvident) || 0) > 0 ||
          (parseFloat(localDeductions?.philhealth) || 0) > 0 ||
          (parseFloat(localDeductions?.pagIbig) || 0) > 0 ||
          (parseFloat(localDeductions?.pagIbig2) || 0) > 0;

        const otherHasAny =
          (parseFloat(localDeductions?.unionDues) || 0) > 0 ||
          (parseFloat(localDeductions?.tax) || 0) > 0 ||
          (localDeductions?.customDeductions || []).some(
            (cd) => parseFloat(cd.amount) > 0
          ) ||
          Number(totalDeductedHours || 0) > 0;

        const pushDedRow = (label, value) => {
          const v = parseFloat(value) || 0;
          if (v <= 0) return;
          doc.text(label, rightX + 3, yRight);
          doc.text(
            formatPeso(v),
            rightX + columnWidth - 2,
            yRight,
            { align: "right" }
          );
          yRight += lineHeight;
        };

        if (govHasAny) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFont);
          doc.setTextColor(100, 100, 100);
          doc.text("Government Contributions:", rightX + 3, yRight);
          yRight += lineHeight;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(bodyFont);
          doc.setTextColor(0, 0, 0);

          pushDedRow("SSS", localDeductions?.sss);
          pushDedRow("SSS Provident", localDeductions?.sssProvident);
          pushDedRow("PhilHealth", localDeductions?.philhealth);
          pushDedRow("Pag-IBIG", localDeductions?.pagIbig);
          pushDedRow("Pag-IBIG 2", localDeductions?.pagIbig2);

          yRight += 2;
        }

        if (otherHasAny) {
          doc.setFont("helvetica", "bold");
          doc.setFontSize(labelFont);
          doc.setTextColor(100, 100, 100);
          doc.text("Other Deductions:", rightX + 3, yRight);
          yRight += lineHeight;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(bodyFont);
          doc.setTextColor(0, 0, 0);

          if (Number(totalDeductedHours || 0) > 0) {
            const hoursStr = Number(totalDeductedHours || 0).toFixed(2);
            const reason =
              localDeductions?.deductedHoursReason &&
              String(localDeductions.deductedHoursReason).trim()
                ? String(localDeductions.deductedHoursReason).trim()
                : "";
            doc.text(
              `Deducted Hours: ${hoursStr} hr(s)`,
              rightX + 5,
              yRight
            );
            yRight += lineHeight;
            if (reason) {
              doc.setFont("helvetica", "italic");
              doc.text(`Reason: ${reason}`, rightX + 5, yRight);
              doc.setFont("helvetica", "normal");
              yRight += lineHeight;
            }
            yRight += 1;
          }

          pushDedRow("Union Dues", localDeductions?.unionDues);
          pushDedRow("Withholding Tax", localDeductions?.tax);

          (localDeductions?.customDeductions || []).forEach((cd) => {
            const amt = parseFloat(cd?.amount) || 0;
            if (!cd?.label || amt <= 0) return;
            doc.text(cd.label, rightX + 3, yRight);
            doc.text(
              formatPeso(amt),
              rightX + columnWidth - 2,
              yRight,
              { align: "right" }
            );
            yRight += lineHeight;
          });
        }

        // Total deductions box
        yRight += 3;
        doc.setDrawColor(200, 50, 50);
        doc.setLineWidth(0.3);
        doc.line(rightX, yRight, rightX + columnWidth, yRight);
        yRight += 4;

        doc.setFillColor(255, 240, 240);
        doc.roundedRect(
          rightX,
          yRight - 4,
          columnWidth,
          7,
          1,
          1,
          "F"
        );
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFont);
        doc.setTextColor(200, 50, 50);
        doc.text("TOTAL DEDUCTIONS:", rightX + 3, yRight + 1);
        doc.text(
          formatPeso(totalDeductions),
          rightX + columnWidth - 2,
          yRight + 1,
          { align: "right" }
        );

        rightRow2Bottom = yRight + 6;
      }

      const row2Bottom = Math.max(leftRow2Bottom, rightRow2Bottom);
      const row3Top = row2Bottom + 20;

      // ===========================
      // ROW 3 LEFT: NET PAY
      // ===========================
      y = row3Top;
      doc.setFillColor(23, 42, 74);
      doc.roundedRect(leftX, y, columnWidth, 10, 2, 2, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFont + 1);
      doc.text("NET PAY", leftX + 3, y + 6);
      doc.text(
        formatPeso(netPay),
        leftX + columnWidth - 2,
        y + 6,
        { align: "right" }
      );

      const leftRow3Bottom = y + 12;

      // ===========================
      // ROW 3 RIGHT: YTD BALANCE
      // ===========================
      const hasLeaveYtd =
        localDeductions?.leaveBalance !== undefined &&
        localDeductions?.leaveBalance !== null &&
        String(silCredits).trim() !== "";
      const hasCustomYtd =
        (localDeductions?.customBalances || []).length > 0;

      yRight = row3Top;

      let rightRow3Bottom = yRight;
      if (hasLeaveYtd || hasCustomYtd) {
        const boxHeight = 50;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.roundedRect(rightX, yRight, columnWidth, boxHeight, 2, 2);

        let yy = yRight + 5;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(titleFont);
        doc.setTextColor(100, 100, 100);
        doc.text("YTD BALANCE", rightX + 3, yy);

        yy += lineHeight;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFont);
        doc.setTextColor(80, 80, 80);
        doc.text("Leave Credits (YTD)", rightX + 5, yy);

        yy += lineHeight;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(bodyFont);
        doc.setTextColor(0, 0, 0);

        if (hasLeaveYtd) {
          doc.text(
            String(silCredits),
            rightX + 7,
            yy
          );
          yy += lineHeight;
        } else {
          doc.setFont("helvetica", "italic");
          doc.setTextColor(120, 120, 120);
          doc.text("No leave credit data", rightX + 7, yy);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          yy += lineHeight;
        }

        yy += 2;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelFont);
        doc.setTextColor(80, 80, 80);
        doc.text("Other YTD Balances", rightX + 5, yy);

        yy += lineHeight;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(bodyFont);
        doc.setTextColor(0, 0, 0);

        if (hasCustomYtd) {
          (localDeductions.customBalances || []).forEach((cb) => {
            const label = (cb?.label || "").trim() || "Unlabeled";
            const valueStr =
              cb?.amount !== undefined && cb?.amount !== null
                ? String(cb.amount)
                : "N/A";
            doc.text(label, rightX + 7, yy);
            doc.text(valueStr, rightX + columnWidth - 2, yy, {
              align: "right",
            });
            yy += lineHeight;
          });
        } else {
          doc.setFont("helvetica", "italic");
          doc.setTextColor(120, 120, 120);
          doc.text("No additional YTD balances", rightX + 7, yy);
        }

        rightRow3Bottom = yRight + boxHeight;
      }

      const _row3Bottom = Math.max(leftRow3Bottom, rightRow3Bottom);
      // (still plenty of space to bottom of page, but we don't need row4)

      // ===========================
      // FOOTER: PAGE NUMBER
      // ===========================
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 287, {
          align: "center",
        });
      }

      const safeName =
        searchFilter?.employee?.name?.replace(/\s+/g, "_") ||
        "employee";
      const dateStr = new Date().toISOString().split("T")[0];
      const filename = `FINAL_CALCULATION_${safeName}_${dateStr}.pdf`;

      doc.save(filename);
    } catch (err) {
      console.error("PDF export error:", err);
    }
  };



  // ==== RENDER ====
  const noData = !calculationData;

  return (
    <BaseWrapper>
      {noData ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Payroll Final Calculation
          </Typography>
          <Typography sx={{ mb: 2 }}>
            No calculation data was passed to this page. Please go back to the
            Payroll Reports page and generate the final calculation again.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/payroll-reports")}
          >
            Back to Payroll Reports
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                Payroll Final Calculation
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchFilter?.employee?.name || "N/A"} •{" "}
                {searchFilter?.startDate} to {searchFilter?.endDate}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/payroll-reports")}
              >
                Back to Payroll Reports
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleOpenExportMenu}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenu}
                open={Boolean(exportMenu)}
                onClose={handleCloseExportMenu}
              >
                <MenuItem
                  onClick={() => {
                    handleFinalCSVExport();
                    handleCloseExportMenu();
                  }}
                >
                  <TableViewIcon fontSize="small" sx={{ mr: 1 }} />
                  Export Final Calculation (CSV)
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleFinalPDFExport();
                    handleCloseExportMenu();
                  }}
                >
                  <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
                  Export Final Calculation (PDF)
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* HOURS + PAY SUMMARY CARD */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2}>
              {/* LEFT: Hours breakdown */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold" }}
                >
                  Hours Breakdown
                </Typography>
                <Typography>
                  Normal Hours: {safeBreakdownTotals.normalHours.toFixed(2)}
                </Typography>
                <Typography>
                  Night Differential:{" "}
                  {safeBreakdownTotals.nightDifferential.toFixed(2)}
                </Typography>
                {/* <Typography>
                  Regular Night Differential:{" "}
                  {safeBreakdownTotals.regularNightDifferential.toFixed(2)}
                </Typography> */}
                <Typography>
                  Overtime: {safeBreakdownTotals.overtimeHours.toFixed(2)}
                </Typography>
                <Typography>
                  Regular Holiday:{" "}
                  {safeBreakdownTotals.regularHolidayHours.toFixed(2)}
                </Typography>
                <Typography>
                  Special Non-Working Holiday:{" "}
                  {safeBreakdownTotals.specialHolidayHours.toFixed(2)}
                </Typography>
                {customEntriesArray.map(([label, hours]) => (
                  <Typography key={label}>
                    {label}: {Number(hours || 0).toFixed(2)}
                  </Typography>
                ))}
                <Typography sx={{ mt: 1, fontWeight: "bold" }}>
                  Total Distributed Work Hours:{" "}
                  {Number(grandTotalHours || 0).toFixed(2)}
                </Typography>
                {totalAddWorkHours ? (
                  <Typography sx={{ fontStyle: "italic" }}>
                    Manually Added Work Hours:{" "}
                    {Number(totalAddWorkHours).toFixed(2)}
                  </Typography>
                ) : null}
                <Typography sx={{ fontStyle: "italic" }}>
                  Unique Total Work Hours:{" "}
                  {Number(uniqueGrandTotalHours || 0).toFixed(2)}
                </Typography>
                <Typography sx={{ mt: 1, fontWeight: "bold" }}>
                  Total Deducted Hours:{" "}
                  {Number(totalDeductedHours || 0).toFixed(2)}                
                  <Tooltip title="Excluded from Net Pay calculation.">
                      <InfoOutlinedIcon fontSize="small" />
                  </Tooltip>
                </Typography>
                {/* <Typography sx={{ fontStyle: "italic" }}>
                  (Excluded from Gross Pay calculation)
                </Typography> */}
              </Grid>

              {/* RIGHT: Pay summary */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold" }}
                >
                  Pay Summary
                </Typography>
                <Typography>
                  Gross Pay (from basic rate x hours x multipliers): PHP{" "}
                  {formatWithCommas(grossPay)}
                </Typography>
                <Typography>
                  Additional Income: PHP {formatWithCommas(totalAdditional)}
                </Typography>
                <Typography>
                  Total Deductions: PHP {formatWithCommas(totalDeductions)}
                </Typography>
                <Typography sx={{ mt: 1, fontWeight: "bold" }}>
                  Net Pay: PHP {formatWithCommas(netPay)}
                </Typography>

                {/* Pay Date input */}
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Pay Date"
                    type="date"
                    size="small"
                    value={payDate}
                    onChange={(e) => setPayDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="This date will be used in the PDF export."
                  />
                </Box>
              </Grid>
            </Grid>
          </Card>

          {/* RATE MULTIPLIERS CARD */}
          <Card sx={{ p: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Rate Multipliers
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Basic Rate (₱ per hour)"
                  type="number"
                  value={basicRate}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Auto-populated from technician compensation"
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Normal Hours Multiplier"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={localRateMultipliers.normalHours}
                  onChange={(e) =>
                    setLocalRateMultipliers((prev) => ({
                      ...prev,
                      normalHours: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Night Differential Multiplier"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={localRateMultipliers.nightDifferential}
                  onChange={(e) =>
                    setLocalRateMultipliers((prev) => ({
                      ...prev,
                      nightDifferential: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Overtime Multiplier"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={localRateMultipliers.overtime}
                  onChange={(e) =>
                    setLocalRateMultipliers((prev) => ({
                      ...prev,
                      overtime: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Regular Holiday Multiplier"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={localRateMultipliers.regularHoliday}
                  onChange={(e) =>
                    setLocalRateMultipliers((prev) => ({
                      ...prev,
                      regularHoliday: e.target.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Special Non-Working Holiday Multiplier"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={localRateMultipliers.specialHoliday}
                  onChange={(e) =>
                    setLocalRateMultipliers((prev) => ({
                      ...prev,
                      specialHoliday: e.target.value,
                    }))
                  }
                />
              </Grid>

              {customEntriesArray.map(([label]) => (
                <Grid item xs={12} md={6} key={label}>
                  <TextField
                    fullWidth
                    label={`${label} Multiplier`}
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={localRateMultipliers.customRates[label] ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLocalRateMultipliers((prev) => ({
                        ...prev,
                        customRates: {
                          ...prev.customRates,
                          [label]: value,
                        },
                      }));
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography
              variant="body2"
              sx={{ mt: 2, color: "text.secondary" }}
            >
              Adjust the basic rate and multipliers as needed. The gross pay,
              additional income, deductions, and net pay above update
              automatically. CSV/PDF exports will use the current values.
            </Typography>
          </Card>

          {/* ADDITIONAL INCOME CARD */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Additional Income
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Allowance"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  value={(localAdditionalIncomes.allowance)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    handleAdditionalIncomeChange("allowance", raw);
                  }}
                  onBlur={(e) =>
                    handleAdditionalIncomeChange(
                      "allowance",
                      parseNumberInput(e.target.value)
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Bonus"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  value={(localAdditionalIncomes.bonus)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    handleAdditionalIncomeChange("bonus", raw);
                  }}
                  onBlur={(e) =>
                    handleAdditionalIncomeChange(
                      "bonus",
                      parseNumberInput(e.target.value)
                    )
                  }
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="13th Month Pay"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  value={(localAdditionalIncomes.thirteenthMonthPay)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    handleAdditionalIncomeChange("thirteenthMonthPay", raw);
                  }}
                  onBlur={(e) =>
                    handleAdditionalIncomeChange(
                      "thirteenthMonthPay",
                      parseNumberInput(e.target.value)
                    )
                  }
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Custom Additional Income
              </Typography>

              {(localAdditionalIncomes.customIncomes || []).map(
                (income, index) => (
                  <Grid
                    container
                    spacing={1}
                    key={index}
                    sx={{ mb: 1, alignItems: "center" }}
                  >
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Label"
                        value={income.label}
                        onChange={(e) =>
                          handleCustomIncomeChange(
                            index,
                            "label",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Amount"
                        inputProps={{ step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              PHP
                            </InputAdornment>
                          ),
                        }}
                        value={(income.amount)}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/,/g, "");
                          handleCustomIncomeChange(index, "amount", raw);
                        }}
                        onBlur={(e) =>
                          handleCustomIncomeChange(
                            index,
                            "amount",
                            parseNumberInput(e.target.value)
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        aria-label="Remove"
                        onClick={() => handleRemoveCustomIncome(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )
              )}

              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 1 }}
                onClick={handleAddCustomIncome}
              >
                Add Custom Income
              </Button>
            </Box>
          </Card>

          {/* DEDUCTIONS CARD */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Deductions
            </Typography>

            <Grid container spacing={2}>
              {/* Informational total deducted hours + reason */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Total Deducted Hours"
                  type="number"
                  value={Number(totalDeductedHours || 0).toFixed(2)}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reason for Deducted Hours"
                  value={localDeductions.deductedHoursReason}
                  onChange={(e) =>
                    handleDeductionChange(
                      "deductedHoursReason",
                      e.target.value
                    )
                  }
                />
              </Grid>

              {/* Standard deductions */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="SSS"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.sss}
                  // onChange={(e) =>
                  //   handleDeductionChange("sss", e.target.value)
                  // }
                  value={(localDeductions.sss)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, sss: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, sss: parsed }));
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="SSS Provident"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.sssProvident}
                  // onChange={(e) =>
                  //   handleDeductionChange("sssProvident", e.target.value)
                  // }
                  value={(localDeductions.sssProvident)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, sssProvident: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, sssProvident: parsed }));
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="PhilHealth"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.philhealth}
                  // onChange={(e) =>
                  //   handleDeductionChange("philhealth", e.target.value)
                  value={(localDeductions.philhealth)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, philhealth: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, philhealth: parsed }));
                  }}                  
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pag-IBIG"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.pagIbig}
                  // onChange={(e) =>
                  //   handleDeductionChange("pagIbig", e.target.value)
                  // }
                  value={(localDeductions.pagIbig)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, pagIbig: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, pagIbig: parsed }));
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Union Dues"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.unionDues}
                  // onChange={(e) =>
                  //   handleDeductionChange("unionDues", e.target.value)
                  // }
                  value={(localDeductions.unionDues)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, unionDues: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, unionDues: parsed }));
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tax"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.tax}
                  // onChange={(e) =>
                  //   handleDeductionChange("tax", e.target.value)
                  // }
                  value={(localDeductions.tax)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, tax: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, tax: parsed }));
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pag-IBIG 2"
                  // type="number"
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">PHP</InputAdornment>
                    ),
                  }}
                  // value={localDeductions.pagIbig2}
                  // onChange={(e) =>
                  //   handleDeductionChange("pagIbig2", e.target.value)
                  // }
                  value={(localDeductions.pagIbig2)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "");
                    setLocalDeductions(prev => ({ ...prev, pagIbig2: raw }));
                  }}
                  onBlur={(e) => {
                    const parsed = parseNumberInput(e.target.value);
                    setLocalDeductions(prev => ({ ...prev, pagIbig2: parsed }));
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Custom Deductions
              </Typography>

              {(localDeductions.customDeductions || []).map(
                (ded, index) => (
                  <Grid
                    container
                    spacing={1}
                    key={index}
                    sx={{ mb: 1, alignItems: "center" }}
                  >
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Label"
                        value={ded.label}
                        onChange={(e) =>
                          handleCustomDeductionChange(
                            index,
                            "label",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Amount"
                        inputProps={{ step: "0.01" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              PHP
                            </InputAdornment>
                          ),
                        }}
                        value={(ded.amount)}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/,/g, "");
                          handleCustomDeductionChange(index, "amount", raw);
                        }}
                        onBlur={(e) =>
                          handleCustomDeductionChange(
                            index,
                            "amount",
                            parseNumberInput(e.target.value)
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        aria-label="Remove"
                        onClick={() => handleRemoveCustomDeduction(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                )
              )}

              <Button
                size="small"
                startIcon={<AddIcon />}
                sx={{ mt: 1 }}
                onClick={handleAddCustomDeduction}
              >
                Add Custom Deduction
              </Button>
            </Box>
          </Card>

          {/* === YTD BALANCE CARD (reference only, not included in total deductions) === */}
          <Card sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Year-to-Date (YTD) Balances
              <Tooltip title="For tracking year-to-date balances only (not included in Deductions total). e.g. Leave Credits, Loan Balance, etc.">
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </Typography>            

            <Grid container spacing={2}>
              {/* Leave Credits (YTD) */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: "text.secondary" }}
                >
                  Leave Credits (YTD)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={silCredits}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Auto-populated from technician compensation"
                  sx={{
                    "& .MuiInputBase-input": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Grid>

              {/* Custom YTD Balances */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    Other YTD Balances
                  </Typography>                  
                </Box>

                {(localDeductions.customBalances || []).map((balance, index) => (
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                    key={index}
                  >
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Label (e.g. SSS/Pag-ibig/Company Loan Balance, Sick Leave Balance, etc.)"
                        value={balance.label}
                        onChange={(e) =>
                          handleCustomYtdBalanceChange(
                            index,
                            "label",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={9} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Amount"
                        value={balance.amount}
                        onChange={(e) =>
                          handleCustomYtdBalanceChange(
                            index,
                            "amount",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={3} md={2}>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveCustomYtdBalance(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddCustomYtdBalance}
                  sx={{ mt: 1 }}
                >
                  Add Custom YTD Balance
                </Button>

                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1, color: "text.secondary" }}
                >
                  Note: YTD balances are for reference only and are not included
                  in the Deductions total above.
                </Typography>
              </Grid>
            </Grid>
          </Card>
          
        </Box>
      )}
    </BaseWrapper>
  );
}

export default PayrollFinalCalculation;
